import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Package,
  TrendingUp,
  Download,
  Clock,
  Plus,
  Image,
  Shield,
  BookOpen,
  Zap,
  HelpCircle,
  ArrowRight,
  FileText,
  Sparkles,
  Wand2,
  Palette,
  Printer,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import Button from '../../components/Button';
import LicenseUpgradeModal from '../../components/LicenseUpgradeModal';
import { useAuth } from '../../contexts/AuthContext';
import { useMockups } from '../../hooks/useSupabase';

const Dashboard: React.FC = () => {
  const { profile, user } = useAuth();
  const { mockups, loading, refetch } = useMockups();
  const navigate = useNavigate();
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);

  React.useEffect(() => {
    refetch();
  }, []);

  const stats = useMemo(() => {
    const totalMockups = mockups.length;
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const thisMonthMockups = mockups.filter((mockup) => {
      const createdDate = new Date(mockup.created_at);
      return createdDate.getMonth() === currentMonth && createdDate.getFullYear() === currentYear;
    }).length;

    const totalDownloads = mockups.reduce((sum, mockup) => sum + (mockup.downloads || 0), 0);
    const uniqueTemplates = new Set(mockups.map((mockup) => mockup.template_id).filter(Boolean)).size;

    return [
      {
        name: 'Total Mockups',
        value: totalMockups.toString(),
        change: thisMonthMockups > 0 ? `+${thisMonthMockups} this month` : 'No change',
        icon: <Package className="h-6 w-6" />,
        changeType: thisMonthMockups > 0 ? 'positive' : 'neutral',
      },
      {
        name: 'Credits Remaining',
        value: (profile?.credits_balance || 0).toString(),
        change: 'credits available',
        icon: <TrendingUp className="h-6 w-6" />,
        changeType: (profile?.credits_balance || 0) > 0 ? 'positive' : 'negative',
      },
      {
        name: 'Downloads',
        value: totalDownloads.toString(),
        change: 'Total downloads',
        icon: <Download className="h-6 w-6" />,
        changeType: 'neutral',
      },
      {
        name: 'Templates Used',
        value: uniqueTemplates.toString(),
        change: 'unique templates',
        icon: <Clock className="h-6 w-6" />,
        changeType: 'neutral',
      },
    ];
  }, [mockups, profile]);

  const recentMockups = useMemo(() => {
    return mockups.slice(0, 4).map((mockup) => ({
      id: mockup.id,
      name: mockup.name,
      template: mockup.template?.name || 'Unknown',
      createdAt: formatDistanceToNow(new Date(mockup.created_at), { addSuffix: true }),
      thumbnail: mockup.mockup_url,
      templateLabel: getTemplateLabel(mockup.template?.category),
    }));
  }, [mockups]);

  const featuredWorkflows = [
    {
      title: 'Brand Team',
      description: 'Build strategy, prompts, and deliverables in the guided multi-agent workspace.',
      cta: 'Open Brand Team',
      path: '/dashboard/brand-team',
      icon: <Sparkles className="h-5 w-5" />,
      accent: 'from-violet-600 to-indigo-600',
      surface: 'bg-violet-50 text-violet-700',
    },
    {
      title: 'Brand Craft',
      description: 'Create a logo, palette, typography system, and branded previews in one flow.',
      cta: 'Open Brand Craft',
      path: '/dashboard/brand-craft',
      icon: <Wand2 className="h-5 w-5" />,
      accent: 'from-sky-600 to-cyan-500',
      surface: 'bg-sky-50 text-sky-700',
    },
    {
      title: 'Your Brand',
      description: 'Keep your saved logo, brand colors, and core settings consistent across every output.',
      cta: 'Open Your Brand',
      path: '/dashboard/brand',
      icon: <Palette className="h-5 w-5" />,
      accent: 'from-amber-500 to-orange-500',
      surface: 'bg-amber-50 text-amber-700',
    },
  ];

  function getTemplateLabel(category?: string) {
    switch (category) {
      case 'software':
        return 'SW';
      case 'apparel':
        return 'AP';
      case 'branding':
        return 'BR';
      case 'books':
        return 'BK';
      case 'marketing':
        return 'MK';
      case 'packaging':
        return 'PK';
      default:
        return 'TM';
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-6 py-8 sm:px-8 lg:px-10">
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="mb-2 font-heading text-h2 text-text-dark">
                Welcome back
                {(profile?.full_name || (user as any)?.user_metadata?.full_name || (user as any)?.user_metadata?.name)
                  ? `, ${profile?.full_name || (user as any)?.user_metadata?.full_name || (user as any)?.user_metadata?.name}`
                  : ''}
              </h1>
              <p className="text-body text-body-grey">
                Start with the brand systems first, then move into templates and production.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="primary"
                size="small"
                onClick={() => setShowUpgradeModal(true)}
                className="flex items-center gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Upgrade
              </Button>
              {(profile?.role === 'admin' || profile?.role === 'super_admin') && (
                <Link to="/admin">
                  <Button variant="secondary" className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Admin Panel
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="mb-8">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Priority Workflows</h2>
              <p className="mt-1 text-sm text-gray-500">Brand Team and Brand Craft are now the fastest paths into the product.</p>
            </div>
            <Link to="/dashboard" className="text-sm font-medium text-brand-purple hover:text-brand-purple/80">
              Dashboard Home
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            {featuredWorkflows.map((workflow) => (
              <button
                key={workflow.title}
                onClick={() => navigate(workflow.path)}
                className="group overflow-hidden rounded-2xl border border-gray-200 bg-white text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-brand-purple/30 hover:shadow-lg"
              >
                <div className={`h-1 w-full bg-gradient-to-r ${workflow.accent}`} />
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl ${workflow.surface}`}>
                      {workflow.icon}
                    </div>
                    <ArrowRight className="h-5 w-5 text-gray-300 transition-all group-hover:translate-x-1 group-hover:text-brand-purple" />
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-gray-900">{workflow.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-gray-600">{workflow.description}</p>
                  <div className="mt-5 inline-flex items-center text-sm font-semibold text-brand-purple">{workflow.cta}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => (
            <div key={stat.name} className="rounded-xl bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div className="rounded-lg bg-primary-50 p-2 text-brand-purple">{stat.icon}</div>
                <span
                  className={`text-sm font-medium ${
                    stat.changeType === 'positive'
                      ? 'text-green-600'
                      : stat.changeType === 'negative'
                      ? 'text-red-600'
                      : 'text-gray-600'
                  }`}
                >
                  {stat.change}
                </span>
              </div>
              <h3 className="mb-1 text-2xl font-bold text-text-dark">{stat.value}</h3>
              <p className="text-sm text-body-grey">{stat.name}</p>
            </div>
          ))}
        </div>

        <div className="mb-8 rounded-2xl bg-gradient-to-r from-brand-purple to-brand-blue p-8 text-white">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <div>
              <h2 className="mb-2 font-heading text-h3">Move from brand system to production</h2>
              <p className="text-lg opacity-90">Start a new mockup once your brand foundation is ready.</p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button variant="secondary" size="large" onClick={() => navigate('/dashboard/brand-team')}>
                <Sparkles className="mr-2 h-5 w-5" />
                Launch Brand Team
              </Button>
              <Button
                variant="secondary"
                size="large"
                onClick={() => {
                  sessionStorage.clear();
                  navigate('/dashboard/mockups');
                }}
              >
                <Plus className="mr-2 h-5 w-5" />
                Create New Mockup
              </Button>
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-white shadow-sm">
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-text-dark">Recent Mockups</h2>
              <Link to="/dashboard/library" className="flex items-center text-sm font-medium text-brand-purple hover:text-brand-purple/80">
                View All
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-purple"></div>
              </div>
            ) : recentMockups.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                {recentMockups.map((mockup) => (
                  <Link key={mockup.id} to={`/dashboard/library?preview=${mockup.id}`} className="group block">
                    <div className="rounded-lg border border-gray-200 p-4 transition-all group-hover:border-brand-purple/30 group-hover:shadow-md">
                      <div className="mb-3 flex aspect-square items-center justify-center rounded-lg bg-gray-100">
                        {mockup.thumbnail ? (
                          <img src={mockup.thumbnail} alt={mockup.name} className="h-full w-full rounded-lg object-contain" />
                        ) : (
                          <span className="text-lg font-semibold tracking-[0.24em] text-gray-400">{mockup.templateLabel}</span>
                        )}
                      </div>
                      <h3 className="mb-1 truncate font-medium text-text-dark group-hover:text-brand-purple">{mockup.name}</h3>
                      <p className="truncate text-sm text-body-grey">{mockup.template}</p>
                      <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
                        <span>{mockup.createdAt}</span>
                        <span className="font-medium text-brand-purple opacity-0 transition-opacity group-hover:opacity-100">Preview</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center">
                <Package className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                <p className="mb-4 text-gray-500">No mockups yet</p>
                <Button
                  variant="primary"
                  size="medium"
                  onClick={() => {
                    sessionStorage.clear();
                    navigate('/dashboard/mockups');
                  }}
                >
                  Create Your First Mockup
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
            <div className="mb-4 flex items-center">
              <div className="mr-4 rounded-lg bg-green-100 p-3">
                <BookOpen className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Quick Start Guide</h3>
                <p className="text-sm text-gray-600">Get started in under 5 minutes</p>
              </div>
            </div>

            <div className="mb-6 space-y-3">
              <div className="flex items-center text-sm">
                <div className="mr-3 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-purple/10">
                  <span className="font-semibold text-brand-purple">1</span>
                </div>
                <p className="text-gray-700">Choose a template from the template library.</p>
              </div>
              <div className="flex items-center text-sm">
                <div className="mr-3 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-purple/10">
                  <span className="font-semibold text-brand-purple">2</span>
                </div>
                <p className="text-gray-700">Upload your design assets or saved logo.</p>
              </div>
              <div className="flex items-center text-sm">
                <div className="mr-3 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-purple/10">
                  <span className="font-semibold text-brand-purple">3</span>
                </div>
                <p className="text-gray-700">Customize, generate, then save finished outputs to the library.</p>
              </div>
            </div>

            <Button variant="primary" size="small" className="w-full" onClick={() => navigate('/dashboard/mockups')}>
              Start Creating
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
            <div className="mb-4 flex items-center">
              <div className="mr-4 rounded-lg bg-blue-100 p-3">
                <Sparkles className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Recommended Next Steps</h3>
                <p className="text-sm text-gray-600">Jump into the areas that improve output quality fastest.</p>
              </div>
            </div>

            <div className="mb-6 space-y-3">
              <button
                onClick={() => navigate('/dashboard/brand-team')}
                className="group flex w-full items-center justify-between rounded-lg bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100"
              >
                <div className="flex items-center">
                  <Sparkles className="mr-3 h-5 w-5 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Plan deliverables in Brand Team</p>
                    <p className="text-xs text-gray-500">Best for campaign systems and multi-output work.</p>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 transition-colors group-hover:text-brand-purple" />
              </button>

              <button
                onClick={() => navigate('/dashboard/brand-craft')}
                className="group flex w-full items-center justify-between rounded-lg bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100"
              >
                <div className="flex items-center">
                  <Wand2 className="mr-3 h-5 w-5 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Shape the identity in Brand Craft</p>
                    <p className="text-xs text-gray-500">Logo, colors, typography, and brand previews.</p>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 transition-colors group-hover:text-brand-purple" />
              </button>

              <button
                onClick={() => navigate('/dashboard/support')}
                className="group flex w-full items-center justify-between rounded-lg bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100"
              >
                <div className="flex items-center">
                  <HelpCircle className="mr-3 h-5 w-5 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Open support resources</p>
                    <p className="text-xs text-gray-500">FAQs, contact options, and valid next actions.</p>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 transition-colors group-hover:text-brand-purple" />
              </button>
            </div>

            <Button variant="secondary" size="small" className="w-full" onClick={() => navigate('/dashboard/brand')}>
              Review Brand Settings
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <button
            onClick={() => navigate('/dashboard/library')}
            className="group rounded-xl border border-gray-200 bg-white p-6 transition-all hover:border-brand-purple hover:shadow-lg"
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="rounded-lg bg-purple-100 p-3">
                <Image className="h-6 w-6 text-purple-600" />
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400 transition-all group-hover:translate-x-1 group-hover:text-brand-purple" />
            </div>
            <h3 className="mb-1 font-semibold text-gray-900">Open Library</h3>
            <p className="text-sm text-gray-600">Review, preview, and download your generated mockups.</p>
          </button>

          <button
            onClick={() => navigate('/dashboard/assets')}
            className="group rounded-xl border border-gray-200 bg-white p-6 transition-all hover:border-brand-purple hover:shadow-lg"
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="rounded-lg bg-orange-100 p-3">
                <FileText className="h-6 w-6 text-orange-600" />
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400 transition-all group-hover:translate-x-1 group-hover:text-brand-purple" />
            </div>
            <h3 className="mb-1 font-semibold text-gray-900">Manage Assets</h3>
            <p className="text-sm text-gray-600">Upload and organize the source files used across your mockups.</p>
          </button>

          <button
            onClick={() => navigate('/dashboard/mockup-to-print')}
            className="group rounded-xl border border-gray-200 bg-white p-6 transition-all hover:border-brand-purple hover:shadow-lg"
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="rounded-lg bg-emerald-100 p-3">
                <Printer className="h-6 w-6 text-emerald-600" />
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400 transition-all group-hover:translate-x-1 group-hover:text-brand-purple" />
            </div>
            <h3 className="mb-1 font-semibold text-gray-900">Mockup to Print</h3>
            <p className="text-sm text-gray-600">Turn saved mockups into print-ready packages with bleed, proofing, and export specs.</p>
          </button>

          <button
            onClick={() => navigate('/dashboard/support')}
            className="group rounded-xl border border-gray-200 bg-white p-6 transition-all hover:border-brand-purple hover:shadow-lg"
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="rounded-lg bg-blue-100 p-3">
                <HelpCircle className="h-6 w-6 text-blue-600" />
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400 transition-all group-hover:translate-x-1 group-hover:text-brand-purple" />
            </div>
            <h3 className="mb-1 font-semibold text-gray-900">Get Help</h3>
            <p className="text-sm text-gray-600">Contact support or browse the help resources without leaving the app.</p>
          </button>
        </div>

        <div className="mt-8 rounded-xl border border-gray-100 bg-gradient-to-r from-brand-purple/5 to-brand-blue/5 p-6">
          <div className="mb-4 flex items-center">
            <Zap className="mr-2 h-5 w-5 text-brand-purple" />
            <h3 className="font-semibold text-gray-900">Quality Tips</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
            <div className="flex items-start">
              <div className="mr-3 mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-brand-purple"></div>
              <p className="text-gray-700">Use Brand Team for campaign planning when you need coordinated outputs instead of one-off mockups.</p>
            </div>
            <div className="flex items-start">
              <div className="mr-3 mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-brand-purple"></div>
              <p className="text-gray-700">Keep your saved logo and brand colors current so templates, SceneScaper, and downloads stay consistent.</p>
            </div>
            <div className="flex items-start">
              <div className="mr-3 mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-brand-purple"></div>
              <p className="text-gray-700">Upload high-resolution assets before generating mockups to reduce cleanup and improve final quality.</p>
            </div>
          </div>
        </div>

        <LicenseUpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          profile={profile}
        />
      </div>
    </div>
  );
};

export default Dashboard;
